import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const MyContext = createContext();


export function MyProvider({ children, navigate }) {
  const [users, setUsers] = useState([]);
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    role: null,
    userId: null,
    loading: true,
  });

  useEffect(() => {
    const apiBaseUrl = process.env.REACT_APP_BASE_URL;
    const publicAuthPaths = ['/login', '/send-otp', '/verify-otp', '/update-password'];

    const isProtectedApiRequest = (url) => {
      if (!apiBaseUrl || !url || !String(url).startsWith(apiBaseUrl)) {
        return false;
      }

      return !publicAuthPaths.some(path => String(url).includes(path));
    };

    const forceLogout = () => {
      setAuthState({
        isAuthenticated: false,
        role: null,
        userId: null,
        loading: false,
      });
      navigate('/', { replace: true });
    };

    const originalFetch = window.fetch;
    const authenticatedFetch = async (...args) => {
      const response = await originalFetch(...args);
      const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url;

      if (response.status === 401 && isProtectedApiRequest(requestUrl)) {
        forceLogout();
      }

      return response;
    };

    window.fetch = authenticatedFetch;

    const axiosInterceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && isProtectedApiRequest(error.config?.url)) {
          forceLogout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      if (window.fetch === authenticatedFetch) {
        window.fetch = originalFetch;
      }
      axios.interceptors.response.eject(axiosInterceptor);
    };
  }, [navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/me`, {
          method: "GET",
          credentials: "include",
        });
        
        if (response.ok) {
          const data = await response.json();
          setAuthState({
            isAuthenticated: true,
            role: data.role,
            userId: data.id,
            loading: false,
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            role: null,
            userId: null,
            loading: false,
          });
        }
      } catch (error) {
        console.error("Error checking auth:", error);
        setAuthState(prev => ({ ...prev, loading: false }));
      }
    };
    checkAuth();
  }, []);

  const updateAuth = (isAuthenticated, role, userId) => {
    setAuthState({ isAuthenticated, role, userId, loading: false });
  };

  const logout = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/logout`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        setAuthState({
          isAuthenticated: false,
          role: null,
          userId: null,
          loading: false
        });
        navigate('/', { replace: true });
      } else {
        throw new Error('Logout failed');
      }
    } catch (error) {
      console.error("Logout failed:", error);
      setAuthState({
        isAuthenticated: false,
        role: null,
        userId: null,
        loading: false
      });
      navigate('/', { replace: true });
    }
  };

  return (
    <MyContext.Provider value={{ users, setUsers, authState, updateAuth, logout }}>
      {children}
    </MyContext.Provider>
  );
}

export function useMyContext() {
  const context = useContext(MyContext);
  if (context === undefined) {
    throw new Error('useMyContext must be used within a MyProvider');
  }
  return context;
}
